// FE-G1: Keyshawn guidance v2, the pure half.
//
// Flag, eligibility, canonical identity and whitelisted payload helpers for
// the two chat transports (the global drawer's useKeyshawnChat and Work's
// coaching/sendChatV2). Nothing here reads the DOM or the network; the two
// Redux selector forms read the store only through the user slice's own
// active-context selectors.
//
// The flag is a presentation and request opt-in, never backend authority. The
// backend re-checks every request and may reject a client that looks
// eligible here. With the flag absent or anything but the string "true",
// every legacy request and surface stays exactly as it was.
import { isCredentialBearing } from "../../pages/Dashboard/resolveTrainingEntry";
import {
  selectActiveCohortUser,
  selectActiveSquadId,
} from "../../store/user/slice";
import { findWorkspaceFile } from "../../store/workspace/selectors";

export const GUIDANCE_CONTEXT_VERSION = "2";
export const GUIDANCE_MESSAGE_MAX = 2000;
export const GUIDANCE_DRAFT_MAX = 10000;

export const GUIDANCE_STEP_TYPES = Object.freeze([
  "mission",
  "briefing",
  "planning",
  "task_claim",
  "task_define",
  "reflection",
  "execution",
  "progress_report",
  "deliverable",
]);
/*
 * S5b: Keyshawn beside a squad document. Not one of the nine Work steps, so
 * GUIDANCE_STEP_TYPES is unchanged; it is only accepted by the descriptor and
 * sent through its own whitelisted payload branch.
 */
export const SQUAD_DOCUMENT_STEP = "squad_document";
const STEP_SET = new Set([...GUIDANCE_STEP_TYPES, SQUAD_DOCUMENT_STEP]);
/* Only these two steps may carry the student's own current draft. */
const DRAFT_STEPS = new Set(["reflection", "task_define"]);
const FIELD_KEYS = new Set(["why", "done", "approach"]);

/** Read at call time so a test (or a build) decides, never a module cache. */
export const isGuidanceV2Enabled = () =>
  process.env.REACT_APP_KEYSHAWN_GUIDANCE_V2 === "true";

/** S5b: the Squad document flag. Default off, set nowhere; read at call time. */
export const isSquadDocumentGuidanceEnabled = () =>
  process.env.REACT_APP_KEYSHAWN_SQUAD_DOCUMENT === "true";

/** A positive integer, or null. Never guesses from strings like "ref:3". */
export const positiveIntOrNull = (value) => {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  if (typeof value === "string" && /^[1-9][0-9]*$/.test(value)) {
    const n = Number(value);
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
};

/**
 * THE CONSERVATIVE CLIENT FILTER. Eligible only when the loaded user, the
 * mounted cohort and its first challenge are known, the posture is known and
 * not validation, and the challenge is not credential-bearing. Anything
 * unknown answers not eligible, and the caller shows nothing new and sends
 * nothing new.
 *
 * Experience switcher: the cohort, challenge and squad are the ACTIVE delivery
 * context's (`enrollment`, `squadId`), never `/me`'s singular enrollment or
 * its first squad, which name only one of a learner's experiences. The
 * user-level fields (id, research posture) stay `/me`'s.
 */
export const resolveGuidanceEligibility = (
  user,
  { enrollment = null, squadId = null } = {},
) => {
  if (!isGuidanceV2Enabled()) return { eligible: false, reason: "flag_off" };
  const userId = positiveIntOrNull(user?.id);
  if (!userId) return { eligible: false, reason: "unknown_user" };
  if (!Object.prototype.hasOwnProperty.call(user, "research_cohort")) {
    return { eligible: false, reason: "unknown_posture" };
  }
  if (user.research_cohort === "validation") {
    return { eligible: false, reason: "validation" };
  }
  const cohort = enrollment?.cohort ?? null;
  const cohortId = positiveIntOrNull(cohort?.id);
  const challenge = Array.isArray(cohort?.challenges)
    ? (cohort.challenges[0] ?? null)
    : null;
  const challengeId = positiveIntOrNull(challenge?.id);
  if (!cohortId || !challengeId) {
    return { eligible: false, reason: "unknown_cohort" };
  }
  if (isCredentialBearing(challenge)) {
    return { eligible: false, reason: "credential" };
  }
  return {
    eligible: true,
    reason: null,
    userId,
    cohortId,
    challengeId,
    squadId: positiveIntOrNull(squadId),
  };
};

/** Redux selector forms. Compare `.eligible`; the object is fresh each call. */
export const selectGuidanceEligibility = (state) =>
  resolveGuidanceEligibility(state?.user?.user ?? null, {
    enrollment: selectActiveCohortUser(state),
    squadId: selectActiveSquadId(state),
  });
export const selectGuidanceEligible = (state) =>
  selectGuidanceEligibility(state).eligible === true;

/** Exactly one authored milestone for the capsule, else null (never first). */
export const soleMilestoneIdForCapsule = (milestones, capsuleId) => {
  const capsule = positiveIntOrNull(capsuleId);
  if (!capsule || !Array.isArray(milestones)) return null;
  const matching = milestones.filter(
    (m) => positiveIntOrNull(m?.capsule_id) === capsule,
  );
  return matching.length === 1 ? positiveIntOrNull(matching[0]?.id) : null;
};

/**
 * THE DESCRIPTOR. A frozen, serializable description of what the student is
 * looking at. `localEntityKind`/`localEntityId` are frontend-only and exist to
 * keep two identities apart (two modules with no saved reflection, two
 * planning tasks); they never go on the wire.
 */
export const makeDescriptor = (fields) => {
  const stepType = STEP_SET.has(fields?.stepType) ? fields.stepType : null;
  const pick = (key) => positiveIntOrNull(fields?.[key]);
  const field = FIELD_KEYS.has(fields?.currentField)
    ? fields.currentField
    : null;
  const files = Array.isArray(fields?.fileIds)
    ? fields.fileIds.map(positiveIntOrNull).filter(Boolean).slice(0, 2)
    : [];
  const intent =
    fields?.documentIntent === "inventory" ||
    fields?.documentIntent === "compare"
      ? fields.documentIntent
      : null;
  return Object.freeze({
    stepType,
    userId: pick("userId"),
    challengeId: pick("challengeId"),
    cohortId: pick("cohortId"),
    squadId: pick("squadId"),
    capsuleId: pick("capsuleId"),
    stepEntityId: pick("stepEntityId"),
    localEntityKind:
      typeof fields?.localEntityKind === "string"
        ? fields.localEntityKind
        : null,
    localEntityId:
      fields?.localEntityId === null || fields?.localEntityId === undefined
        ? null
        : String(fields.localEntityId),
    taskId: pick("taskId"),
    moduleId: pick("moduleId"),
    milestoneId: pick("milestoneId"),
    fileIds: Object.freeze(files),
    documentIntent: intent,
    currentField: stepType === "task_define" ? field : null,
    accessGeneration:
      fields?.accessGeneration === null ||
      fields?.accessGeneration === undefined
        ? null
        : String(fields.accessGeneration),
  });
};

/**
 * S5b: a squad_document descriptor names a capsule and exactly one file, which
 * is also its step entity, and never a task, a document intent or a field.
 */
const isSquadDocumentShape = (d) =>
  Boolean(
    d.capsuleId &&
      d.fileIds.length === 1 &&
      d.stepEntityId === d.fileIds[0] &&
      !d.taskId &&
      !d.documentIntent &&
      !d.currentField,
  );

/** A descriptor can only be sent with a step and a capsule or a task. */
export const isDescriptorComplete = (d) =>
  Boolean(
    d &&
      d.stepType &&
      d.userId &&
      d.challengeId &&
      d.cohortId &&
      (d.capsuleId || d.taskId) &&
      (d.stepType !== SQUAD_DOCUMENT_STEP || isSquadDocumentShape(d)),
  );

/**
 * S5b: the descriptor for Keyshawn beside a squad document. The file is the
 * step entity; the capsule and squad come from the workspace context (the
 * squad falls back to the learner's). Nothing else is ever set.
 */
export const makeSquadDocumentDescriptor = ({
  eligibility,
  context,
  fileId,
  accessGeneration,
}) =>
  makeDescriptor({
    userId: eligibility?.userId,
    challengeId: eligibility?.challengeId,
    cohortId: eligibility?.cohortId,
    squadId: positiveIntOrNull(context?.squad_id) ?? eligibility?.squadId,
    capsuleId: context?.capsule_id,
    stepType: SQUAD_DOCUMENT_STEP,
    stepEntityId: fileId,
    fileIds: [fileId],
    accessGeneration,
  });

const KEY_FIELDS = [
  "userId",
  "challengeId",
  "cohortId",
  "squadId",
  "capsuleId",
  "stepType",
  "stepEntityId",
  "localEntityKind",
  "localEntityId",
  "taskId",
  "moduleId",
  "milestoneId",
  "fileIds",
  "documentIntent",
  "currentField",
  "accessGeneration",
];

/** Canonical identity. No draft text, token, URL or document byte. */
export const buildIdentityKey = (d) => {
  if (!d) return null;
  return JSON.stringify(
    KEY_FIELDS.map((key) => {
      const value = d[key];
      return Array.isArray(value) ? value.join(",") : (value ?? null);
    }),
  );
};

/**
 * THE WIRE PAYLOAD, WHITELISTED. Returns `{ ok, payload, draftOmitted }` or
 * `{ ok: false, reason }`. The student's own draft is read by the caller at
 * send time and passed in; only reflection and task_define may carry it.
 * progress_report never carries current_draft, current_field or
 * claimed_concepts, not even as empty values.
 */
export const buildGuidancePayload = ({
  message,
  descriptor,
  draft = null,
  summonSessionId = null,
}) => {
  const text = typeof message === "string" ? message.trim() : "";
  if (!text) return { ok: false, reason: "empty" };
  if (text.length > GUIDANCE_MESSAGE_MAX) {
    return { ok: false, reason: "message_too_long" };
  }
  if (!isDescriptorComplete(descriptor)) {
    return { ok: false, reason: "incomplete_identity" };
  }
  const d = descriptor;
  if (d.stepType === SQUAD_DOCUMENT_STEP) {
    // S5b: an explicit whitelist that never reaches the task, intent, draft
    // or summon lines below.
    if (!isGuidanceV2Enabled() || !isSquadDocumentGuidanceEnabled()) {
      return { ok: false, reason: "flag_off" };
    }
    if (!isSquadDocumentShape(d) || d.moduleId || d.milestoneId) {
      return { ok: false, reason: "invalid_squad_document" };
    }
    return {
      ok: true,
      payload: {
        message: text,
        context_version: GUIDANCE_CONTEXT_VERSION,
        step_type: SQUAD_DOCUMENT_STEP,
        capsule_id: d.capsuleId,
        step_entity_id: d.fileIds[0],
        workspace_file_id: d.fileIds[0],
      },
      draftOmitted: false,
    };
  }
  const payload = {
    message: text,
    context_version: GUIDANCE_CONTEXT_VERSION,
    step_type: d.stepType,
  };
  if (d.capsuleId) payload.capsule_id = d.capsuleId;
  if (d.taskId) payload.task_id = d.taskId;
  if (d.stepEntityId) payload.step_entity_id = d.stepEntityId;
  if (d.moduleId) payload.module_id = d.moduleId;
  if (d.milestoneId) payload.milestone_id = d.milestoneId;
  if (
    typeof summonSessionId === "string" &&
    /^[0-9a-f-]{36}$/i.test(summonSessionId)
  ) {
    payload.summon_session_id = summonSessionId;
  }

  if (d.documentIntent === "inventory") {
    payload.document_intent = "inventory";
  } else if (d.documentIntent === "compare") {
    const [first, second] = d.fileIds;
    if (!first || !second || first === second) {
      return { ok: false, reason: "invalid_comparison" };
    }
    payload.document_intent = "compare";
    payload.comparison_file_ids = [first, second];
    payload.workspace_file_id = first;
  } else if (d.fileIds.length > 0 && d.stepType === "execution") {
    payload.workspace_file_id = d.fileIds[0];
  }

  let draftOmitted = false;
  if (DRAFT_STEPS.has(d.stepType) && draft && typeof draft === "object") {
    const draftText = typeof draft.text === "string" ? draft.text : "";
    if (draftText.length > GUIDANCE_DRAFT_MAX) {
      draftOmitted = true;
    } else if (draftText.length > 0) {
      payload.current_draft = draftText;
    }
    if (d.stepType === "task_define" && d.currentField) {
      payload.current_field = d.currentField;
    }
    if (Number.isInteger(draft.revisionCount) && draft.revisionCount >= 0) {
      payload.extraPayload = { draft_revision_count: draft.revisionCount };
    }
  }
  return { ok: true, payload, draftOmitted };
};

const SQUAD_AVAILABILITY = new Set(["readable", "partial", "metadata_only"]);

/** The response metadata we keep and render; nothing else from the body. */
export const readGuidanceResponse = (body) => {
  const data = body && typeof body === "object" ? body : {};
  const fallback = data.fallback === true;
  const status = ["complete", "partial", "unavailable"].includes(
    data.context_status,
  )
    ? data.context_status
    : "unavailable";
  const sources =
    fallback || !Array.isArray(data.sources)
      ? []
      : data.sources
          .filter(
            (s) =>
              s &&
              typeof s.type === "string" &&
              typeof s.label === "string" &&
              s.label.trim() !== "",
          )
          .map((s) => ({
            type: s.type,
            entityId: positiveIntOrNull(s.entity_id),
            label: s.label,
          }));
  const actions =
    fallback || !Array.isArray(data.actions) ? [] : data.actions.slice();
  const reply = {
    text:
      (typeof data.response === "string" && data.response) ||
      (typeof data.message === "string" && data.message) ||
      "",
    contextVersion:
      typeof data.context_version === "string" ? data.context_version : null,
    contextStatus: fallback ? "unavailable" : status,
    contextLabel:
      typeof data.context_label === "string" ? data.context_label : null,
    sources,
    actions,
    fallback,
  };
  // S5b: only a squad reply carries this; every other reply keeps its keys.
  const squad = data.squad_document;
  if (squad && typeof squad === "object") {
    reply.squadDocument = {
      fileId: positiveIntOrNull(squad.file_id),
      contentAvailability: SQUAD_AVAILABILITY.has(squad.content_availability)
        ? squad.content_availability
        : null,
    };
  }
  return reply;
};

/*
 * S5b: the one line drawn under a squad reply, from the server's availability
 * only. metadata_only has none: the server's own lead is the disclosure.
 */
export const SQUAD_DOCUMENT_COPY = Object.freeze({
  readable: "Keyshawn can see this document.",
  partial: "Keyshawn can see part of this document.",
});

export const GUIDANCE_COPY = Object.freeze({
  contextRejected:
    "I couldn't use this screen context. Your work is still here.",
  transportFailed: "I couldn't answer just now. Try sending that again.",
  retry: "Try again",
  partial: "Some context wasn't available for this reply.",
  unavailable: "I couldn't check the current work for this reply.",
  tooLong: `Keep your question under ${GUIDANCE_MESSAGE_MAX} characters.`,
  draftOmitted:
    "Your draft is too long to share, so this question goes without it.",
  unavailableHere: "Keyshawn can't use this screen right now.",
  interrupted:
    "You closed this before I answered. Send it again when you're ready.",
  requestedPair: "Asked to compare",
});

/** 404 and 422 are the backend saying this context is not usable. */
export const isContextRejection = (status) => status === 404 || status === 422;

export const isCanceled = (err) =>
  err?.name === "CanceledError" ||
  err?.name === "AbortError" ||
  err?.code === "ERR_CANCELED";

let requestCounter = 0;
/** Unique per client session; ordering does not matter, only identity. */
export const nextGuidanceRequestId = () => {
  requestCounter += 1;
  return `g${Date.now().toString(36)}-${requestCounter}`;
};

/* ===================== FE-G1 WORK DOCUMENT SCOPE =====================
 * Which documents the NEXT Work question may name. Pure: reads the current
 * workspace entry and the provider session, never Drive, never the DOM.
 *
 *   default    the visible primary document, when it is a current-level
 *              file; nothing when it is a reference or not loaded
 *   inventory  no document id at all: the backend lists the authorized
 *              current-level squad files (metadata only)
 *   compare    exactly the visible primary and reference, both current-level
 *              numeric files and distinct; a pairing hidden at narrow width
 *              (canPair false), an empty slot 1, a `ref:` slot, an
 *              earlier-level file or an unavailable row never qualifies. A
 *              stale suspended-pairing record does not veto a visible pair.
 */
export const WORK_SCOPE_COPY = Object.freeze({
  askFiles: "Ask about squad files",
  compare: "Compare open documents",
  clear: "Clear",
  nextFiles: "Next question: squad files",
  nextCompare: (a, b) => `Next question: compare ${a} and ${b}`,
  loading: "The file list is still loading.",
  denied: "These files aren't available to compare here.",
  needTwo: "Open two current-level documents to compare them.",
  makeRoom:
    'You have two documents open. Choose "Make room to compare" in the file menu to see them side by side.',
  wider: "Comparing documents needs a wider window.",
  openAlongside:
    'Choose "Open alongside" in the file menu to put your other document beside this one.',
  referencePrimary: "This reference is not available for document review here.",
});

/* No room facts: room-making counts as unavailable. */
const NO_ROOM = Object.freeze({ roomToCompare: false, roomUsed: false });

const fileTitle = (file) => {
  const raw =
    (typeof file?.name === "string" && file.name.trim()) ||
    (typeof file?.title === "string" && file.title.trim()) ||
    "";
  return raw || "this document";
};

/** A current-level workspace file for an open-document slot, or null. */
export const currentLevelFile = (entry, session, slot) => {
  if (slot === null || slot === undefined) return null;
  if (typeof slot === "string" && slot.startsWith("ref:")) return null;
  const id = positiveIntOrNull(slot);
  if (!id) return null;
  if (session?.unavailable?.[slot] || session?.unavailable?.[id]) return null;
  const file = entry?.files?.byId?.[id];
  if (!file) return null;
  const found = findWorkspaceFile(entry, id);
  // A current file comes back with capsuleId null; an earlier-level file
  // carries its numeric capsule and a reference its source capsule.
  if (!found || found.capsuleId !== null || found.file !== file) return null;
  return { id, title: fileTitle(file) };
};

/**
 * Why compare is refused when two current-level documents are open but no
 * pair is visible (correction pass B). `openCount` counts DISTINCT current
 * files anywhere in `openDocs`, so a retained tab, a suspended pairing (its
 * two ids stay in `openDocs` until dismissed) and a hidden reference all
 * count. `room` is WorkStep's measurement: Make room is named only when it
 * would give room and has not been used already. Both file-menu reasons need
 * a visible current-level primary, because the file menu is that document's.
 */
const pairRefusal = ({ session, primary, openCount, room }) => {
  if (!primary || openCount < 2) return WORK_SCOPE_COPY.needTwo;
  if (session?.canPair === true) return WORK_SCOPE_COPY.openAlongside;
  const facts = room ?? NO_ROOM;
  return facts.roomToCompare === true && facts.roomUsed !== true
    ? WORK_SCOPE_COPY.makeRoom
    : WORK_SCOPE_COPY.wider;
};

export const resolveWorkDocumentScope = ({
  entry,
  session,
  mode = null,
  room = NO_ROOM,
}) => {
  const docs = Array.isArray(session?.openDocs) ? session.openDocs : [];
  const primarySlot = docs[0] ?? null;
  const referenceSlot = docs[1] ?? null;
  const primaryIsReference =
    typeof primarySlot === "string" && primarySlot.startsWith("ref:");
  const primary = currentLevelFile(entry, session, primarySlot);

  let compareBlocked = null;
  let pair = null;
  if (!entry || entry.status !== "enabled") {
    compareBlocked = WORK_SCOPE_COPY.loading;
  } else if (entry.files?.error) {
    compareBlocked = WORK_SCOPE_COPY.denied;
  } else {
    // R2: the visible reference is exactly what WorkspaceView draws beside
    // the primary (`canPair` and slot 1). A stale suspended pairing never
    // vetoes it; it only matters for the reason when slot 1 is empty.
    const reference =
      session?.canPair === true && referenceSlot != null
        ? currentLevelFile(entry, session, referenceSlot)
        : null;
    if (primary && reference && primary.id !== reference.id) {
      pair = [primary, reference];
    } else {
      const openIds = new Set(
        docs
          .map((slot) => currentLevelFile(entry, session, slot)?.id ?? null)
          .filter(Boolean),
      );
      compareBlocked = pairRefusal({
        session,
        primary,
        openCount: openIds.size,
        room,
      });
    }
  }

  const effectiveMode =
    mode === "inventory"
      ? "inventory"
      : mode === "compare" && pair
        ? "compare"
        : null;
  let fileIds = [];
  let documentIntent = null;
  if (effectiveMode === "inventory") {
    documentIntent = "inventory";
  } else if (effectiveMode === "compare") {
    documentIntent = "compare";
    fileIds = [pair[0].id, pair[1].id];
  } else if (primary) {
    fileIds = [primary.id];
  }
  return {
    mode: effectiveMode,
    primary,
    primaryIsReference,
    note:
      primaryIsReference && !effectiveMode
        ? WORK_SCOPE_COPY.referencePrimary
        : null,
    pair,
    compareBlocked,
    fileIds,
    documentIntent,
    // Changes only when what is on screen changes; a mode resets on it.
    slotsKey: `${primarySlot ?? ""}|${referenceSlot ?? ""}`,
  };
};

/**
 * The Work milestone: the chosen output when it is one of this level's
 * outputs, else the owned task's output when it is, else none.
 */
export const workMilestoneId = ({ outputs, selectedOutputId, task }) => {
  const ids = new Set(
    (Array.isArray(outputs) ? outputs : [])
      .map((o) => positiveIntOrNull(o?.milestone_id))
      .filter(Boolean),
  );
  const chosen = positiveIntOrNull(selectedOutputId);
  if (chosen && ids.has(chosen)) return chosen;
  const fed = positiveIntOrNull(task?.milestone_id);
  if (fed && ids.has(fed)) return fed;
  return null;
};
